import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';

function Referrals() {
  const styles = useThemeStyles();
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchReferrers();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [selectedMonth, selectedYear]);

  const fetchReferrers = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (selectedMonth) params.month = selectedMonth;
      if (selectedYear) params.year = selectedYear;
      
      const response = await api.get('/api/admin/referrals/by-referrer', { params });
      setReferrers(response.data.referrers || []);
    } catch (err) {
      setError('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  const months = [
    { value: '', label: 'All Months' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= currentYear - 2; i--) {
    years.push(i);
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container.page}>
      <Typography component="h2" className="pagename" sx={styles.text.pagename}>
        Referrals by Referrer
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Month</InputLabel>
          <Select
            value={selectedMonth}
            label="Month"
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {months.map(month => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={selectedYear}
            label="Year"
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {years.map(year => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {referrers.length === 0 ? (
        <Alert severity="info">No referrals found for the selected period.</Alert>
      ) : (
        <Grid container spacing={3}>
          {referrers.map((referrer) => (
            <Grid item xs={12} md={6} key={referrer.referralCode}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      {referrer.referrerName}
                    </Typography>
                    <Chip label={referrer.referralCode} size="small" />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total Signups
                      </Typography>
                      <Typography variant="h6">
                        {referrer.totalSignups}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Conversions
                      </Typography>
                      <Typography variant="h6">
                        {referrer.conversions}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Upgrades
                      </Typography>
                      <Typography variant="h6">
                        {referrer.upgrades}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Commissions Earned
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        £{referrer.totalCommissionsEarned.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Commissions Paid
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        £{referrer.totalCommissionsPaid.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Status Breakdown
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {referrer.pendingCommissions > 0 && (
                            <Chip 
                              label={`Pending: ${referrer.pendingCommissions}`} 
                              size="small" 
                              color="warning" 
                            />
                          )}
                          {referrer.approvedCommissions > 0 && (
                            <Chip 
                              label={`Approved: ${referrer.approvedCommissions}`} 
                              size="small" 
                              color="info" 
                            />
                          )}
                          {referrer.paidCommissions > 0 && (
                            <Chip 
                              label={`Paid: ${referrer.paidCommissions}`} 
                              size="small" 
                              color="success" 
                            />
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default Referrals;

